import clsx from 'clsx'
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import {useAuth} from '../../../../app/modules/auth'
import {SettingsMenu} from '../../../../app/modules/settings'
import {KTSVG} from '../../../helpers'
import {ROUTES} from '../../../constants/_routes'
import useWindowDimensions from '../../../hooks/useWindowDimensions'

const SettingButton = () => {
  const {currentUser} = useAuth()
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const {width} = useWindowDimensions()
  const [showMenu, setShowMenu] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [transformOrigin, setTransformOrigin] = useState('top right')
  const isCompactSettingsMenu = width < 1400

  const calculateTransformOrigin = useCallback(() => {
    if (!buttonRef.current) return 'top right'

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const buttonCenterX = buttonRect.left + buttonRect.width / 2
    return buttonCenterX > window.innerWidth / 2 ? 'top right' : 'top left'
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  useEffect(() => {
    setShowMenu(false)
  }, [pathname, isCompactSettingsMenu])

  useLayoutEffect(() => {
    if (!isCompactSettingsMenu || !showMenu) return

    const updateTransformOrigin = () => {
      setTransformOrigin(calculateTransformOrigin())
    }

    updateTransformOrigin()
    window.addEventListener('resize', updateTransformOrigin)

    return () => {
      window.removeEventListener('resize', updateTransformOrigin)
    }
  }, [isCompactSettingsMenu, showMenu, calculateTransformOrigin])

  const isSettingsRoute =
    pathname === ROUTES.SETTINGS.PROFILE || pathname.startsWith(`${ROUTES.SETTINGS.BASE}/`)
  const isActive = isSettingsRoute || showMenu

  const handleButtonClick = () => {
    if (isCompactSettingsMenu) {
      setShowMenu((prev) => !prev)
      return
    }

    navigate(ROUTES.SETTINGS.PROFILE)
  }

  if (!currentUser) return null

  return (
    <div className='topbar-dropdown topbar-dropdown--settings' ref={containerRef}>
      <button
        type='button'
        id='gori-settings-dropdown'
        ref={buttonRef}
        className={clsx('btn p-0 ms-2 position-relative topbar-icon-button', {
          'topbar-icon-button--active': isActive,
        })}
        aria-expanded={showMenu}
        aria-haspopup={isCompactSettingsMenu ? 'menu' : undefined}
        onClick={handleButtonClick}
      >
        <KTSVG
          path='/media/gori/topbar/settings.svg'
          className='desktop-only-menu-item'
          small={false}
        />
      </button>

      {isCompactSettingsMenu && (
        <CSSTransition in={showMenu} timeout={600} classNames='pop' unmountOnExit>
          <div
            className='topbar-dropdown-menu topbar-dropdown-menu--settings'
            style={{right: 'calc(100% - 85px)', transformOrigin}}
            role='menu'
            aria-labelledby='gori-settings-dropdown'
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('a')) {
                setShowMenu(false)
              }
            }}
          >
            <div className='topbar-dropdown-menu__content settings-menu-content'>
              <SettingsMenu />
            </div>
          </div>
        </CSSTransition>
      )}
    </div>
  )
}

export {SettingButton}
